import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { Settings, Image as ImageIcon, Save, Monitor, Shield, Globe, Type, Palette, RefreshCw, Upload, Check } from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';
import { useAuth } from '../../../context/AuthContext';
import { resolveBrandingAssetUrl } from '../../../hooks/useBranding';

export default function GlobalSettingsAdminView() {
  const queryClient = useQueryClient();
  const { refreshBranding } = useAuth();
  const [uploading, setUploading] = useState<string | null>(null);

  // Form states
  const [globalAppName, setGlobalAppName] = useState('CYZOR Control');
  const [globalLogoUrl, setGlobalLogoUrl] = useState('');
  const [globalIconUrl, setGlobalIconUrl] = useState('');
  const [loginHeroUrl, setLoginHeroUrl] = useState('');
  const [globalLogoSize, setGlobalLogoSize] = useState('40');
  const [globalIconSize, setGlobalIconSize] = useState('20');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [debugLevel, setDebugLevel] = useState('INFO');
  const [sessionTimeout, setSessionTimeout] = useState(120);

  // Query Settings
  const { isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/settings');
      const payload = res.data?.data ?? res.data;
      if (payload) {
        setGlobalAppName(payload.globalAppName || 'CYZOR Control');
        setGlobalLogoUrl(payload.globalLogoUrl || '');
        setGlobalIconUrl(payload.globalIconUrl || '');
        setLoginHeroUrl(payload.loginHeroUrl || '');
        setGlobalLogoSize(String(payload.globalLogoSize || '40'));
        setGlobalIconSize(String(payload.globalIconSize || '20'));
        setMaintenanceMode(!!payload.maintenanceMode);
        setDebugLevel(payload.debugLevel || 'INFO');
        setSessionTimeout(payload.sessionTimeout || 120);
      }
      return payload;
    },
  });

  // Save Settings Mutation
  const saveMutation = useMutation({
    mutationFn: (data: any) => apiClient.put('/api/admin/settings', data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stripe-config'] });
      showSuccess('Configurações salvas com sucesso!');
      if (refreshBranding) await refreshBranding();
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao salvar configurações.');
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'icon' | 'hero') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(type);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiClient.post('/api/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data ?? res.data;
      if (data && data.url) {
        const assetUrl = resolveBrandingAssetUrl(data.url) || data.url;
        if (type === 'logo') setGlobalLogoUrl(assetUrl);
        if (type === 'icon') setGlobalIconUrl(assetUrl);
        if (type === 'hero') setLoginHeroUrl(assetUrl);
        showSuccess('Imagem enviada com sucesso!');
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || err.message || 'Erro ao fazer upload da imagem.');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      globalAppName,
      globalLogoUrl,
      globalIconUrl,
      loginHeroUrl,
      globalLogoSize,
      globalIconSize,
      maintenanceMode,
      debugLevel,
      sessionTimeout,
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Settings size={14} /> Personalização & White-Label
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Configurações Globais da Plataforma
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Defina o nome do aplicativo, logos corporativas, hero do login e políticas de sessão.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Branding Card */}
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-6">
          <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
            <Palette size={16} className="text-indigo-600" />
            <h3 className="text-sm font-bold text-gray-900">Identidade Visual & Marca</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Nome Global da Aplicação</label>
              <input
                type="text"
                value={globalAppName}
                onChange={(e) => setGlobalAppName(e.target.value)}
                placeholder="Ex: CYZOR Control"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500 font-medium"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tamanho do Logo (px)</label>
                <input
                  type="number"
                  value={globalLogoSize}
                  onChange={(e) => setGlobalLogoSize(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tamanho do Ícone (px)</label>
                <input
                  type="number"
                  value={globalIconSize}
                  onChange={(e) => setGlobalIconSize(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                />
              </div>
            </div>
          </div>

          {/* Uploads Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            {/* Logo */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60 space-y-3">
              <p className="text-xs font-bold text-gray-800">Logo Principal (Topbar)</p>
              {globalLogoUrl && (
                <div className="h-14 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-200">
                  <img src={globalLogoUrl} alt="Logo" className="max-h-full object-contain" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'logo')}
                className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
              />
              {uploading === 'logo' && <p className="text-[10px] text-indigo-600 font-medium">Enviando logo...</p>}
            </div>

            {/* Icon */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60 space-y-3">
              <p className="text-xs font-bold text-gray-800">Favicon / Ícone App</p>
              {globalIconUrl && (
                <div className="h-14 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-200">
                  <img src={globalIconUrl} alt="Ícone" className="max-h-full object-contain" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'icon')}
                className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
              />
              {uploading === 'icon' && <p className="text-[10px] text-indigo-600 font-medium">Enviando ícone...</p>}
            </div>

            {/* Hero Image */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60 space-y-3">
              <p className="text-xs font-bold text-gray-800">Banner da Tela de Login</p>
              {loginHeroUrl && (
                <div className="h-14 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-200">
                  <img src={loginHeroUrl} alt="Hero" className="max-h-full object-contain" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'hero')}
                className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
              />
              {uploading === 'hero' && <p className="text-[10px] text-indigo-600 font-medium">Enviando banner...</p>}
            </div>
          </div>
        </div>

        {/* Security & Operation */}
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 pb-4 border-b border-gray-100">
            <Shield size={16} className="text-indigo-600" />
            <h3 className="text-sm font-bold text-gray-900">Operação & Segurança do Sistema</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Tempo Limite de Sessão (minutos)</label>
              <input
                type="number"
                value={sessionTimeout}
                onChange={(e) => setSessionTimeout(Number(e.target.value))}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Nível de Log / Debug</label>
              <select
                value={debugLevel}
                onChange={(e) => setDebugLevel(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium"
              >
                <option value="INFO">INFO (Padrão)</option>
                <option value="WARN">WARN (Apenas alertas)</option>
                <option value="DEBUG">DEBUG (Detalhado)</option>
              </select>
            </div>
          </div>

          <div className="pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={maintenanceMode}
                onChange={(e) => setMaintenanceMode(e.target.checked)}
                className="rounded-md border-gray-300 text-indigo-600 h-4 w-4"
              />
              <div>
                <p className="text-xs font-bold text-gray-900">Modo de Manutenção Ativo</p>
                <p className="text-[10px] text-gray-500">Exibe aviso de manutenção programada para usuários não-administradores</p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-all disabled:opacity-50"
          >
            <Save size={14} /> {saveMutation.isPending ? 'Salvando...' : 'Salvar Todas as Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
}
